import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  makeStyles,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import AuthContext from "connections/auth/AuthProvider.js";
import { successColor } from "assets/jss/material-dashboard-pro-react.js";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import {
  getSetupkeyMapping,
  saveSetupkeyMapping,
} from "../../viewServices/apiRelationships";

const useStyles = (clientColor) =>
  makeStyles((theme) => ({
    ...customSelectStyle(theme, clientColor),
    headerTitle: {
      ...theme.typography.h6,
      fontSize: "1.125rem",
      color: theme.palette.secondary.main,
      fontWeight: "400",
      marginBottom: "0px",
      marginTop: "4px",
    },
    cancelButton: {
      minWidth: "80px",
      color: "#666",
      borderColor: "#ccc",
      fontSize: "0.8rem",
      padding: theme.spacing(0.5, 1.5),
      "&:hover": {
        backgroundColor: "#f5f5f5",
        borderColor: "#999",
      },
    },
    copyButton: {
      backgroundColor: clientColor,
      color: "#fff",
      minWidth: "100px",
      fontSize: "0.8rem",
      padding: theme.spacing(0.5, 2),
      "&:hover": {
        backgroundColor: successColor[1],
      },
      "&.Mui-disabled": {
        backgroundColor: "#e0e0e0",
        color: "#9e9e9e",
      },
    },
  }));

export default function DialogCopyVariant({
  open,
  onClose,
  scenarioPremises,
  currentPremise,
  onCopySuccess,
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";
  const classes = useStyles(clientColor)();

  const [selectedPremise, setSelectedPremise] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleCopy = async () => {
    setIsLoading(true);

    const toastId = toast.loading("Copying configurations...");

    try {
      // 1. Busca mappings da premise de origem
      const sourceMappings = await getSetupkeyMapping(selectedPremise);

      if (!sourceMappings || Object.keys(sourceMappings).length === 0) {
        toast.update(toastId, {
          render: "No configurations found in source premise",
          type: "warning",
          isLoading: false,
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
        });
        setIsLoading(false);
        return;
      }

      // 2. Salva na premise de destino (atual)
      await saveSetupkeyMapping(currentPremise.premise_key, sourceMappings);

      // 3. Chama callback para refresh
      if (onCopySuccess) {
        await onCopySuccess();
      }

      // 4. Success
      toast.update(toastId, {
        render: `Configurations copied from "${selectedPremise}" successfully!`,
        type: "success",
        isLoading: false,
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
      });

      // Reset e fecha
      setSelectedPremise("");
      onClose();
    } catch (error) {
      console.error("Failed to copy configurations:", error);
      toast.update(toastId, {
        render: "Failed to copy configurations. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedPremise("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      {/* HEADER */}
      <Box
        borderBottom="1px solid #e0e0e0"
        style={{ padding: "8px 16px", position: "relative" }}
      >
        <h4 className={classes.headerTitle}>Select Premise</h4>
      </Box>

      {/* CONTENT */}
      <Box p={3}>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
          style={{ marginBottom: "8px", fontSize: "0.875rem" }}
        >
          Copy all setup key configurations from:
        </Typography>

        <FormControl fullWidth className={classes.selectFormControl}>
          <Select
            value={selectedPremise}
            onChange={(e) => setSelectedPremise(e.target.value)}
            displayEmpty
            disabled={isLoading}
            className={classes.select}
            MenuProps={{
              className: classes.selectMenu,
              classes: { paper: classes.selectPaper },
            }}
          >
            <MenuItem value="" disabled className={classes.selectMenuItem}>
              Select a premise...
            </MenuItem>
            {scenarioPremises
              .filter((p) => p.premise_key !== currentPremise?.premise_key)
              .map((p) => (
                <MenuItem
                  key={p.premise_key}
                  value={p.premise_key}
                  className={classes.selectMenuItem}
                  classes={{
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {p.premise_label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>

      {/* FOOTER */}
      <Box
        p={2}
        display="flex"
        justifyContent="flex-end"
        gap={1}
        borderTop="1px solid #e0e0e0"
        style={{ gap: "8px" }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          className={classes.cancelButton}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          disabled={!selectedPremise || isLoading}
          className={classes.copyButton}
          startIcon={
            isLoading ? (
              <ThreeDots
                color="#ffffff"
                height={16}
                width={16}
                ariaLabel="copying"
              />
            ) : (
              <FileCopyIcon />
            )
          }
        >
          {isLoading ? "Copying..." : "Copy"}
        </Button>
      </Box>
    </Dialog>
  );
}

DialogCopyVariant.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  scenarioPremises: PropTypes.arrayOf(
    PropTypes.shape({
      premise_key: PropTypes.string.isRequired,
      premise_label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  currentPremise: PropTypes.shape({
    premise_key: PropTypes.string.isRequired,
    premise_label: PropTypes.string.isRequired,
  }),
  onCopySuccess: PropTypes.func,
};
